/**
 * KYC CLEANUP DASHBOARD
 * 
 * Admin interface for managing KYC database cleanup after Telegram migration.
 * Provides safe cleanup operations with dry-run capabilities and detailed reporting.
 */

import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface KYCRecord {
  id: string;
  user_id: number;
  kyc_status: string;
  created_by_telegram_id: number | null;
  created_at: string;
  users: {
    username: string;
    email: string;
  };
  documents: string[];
  hasIdDocument: boolean;
  hasAddressProof: boolean;
  hasSelfie: boolean;
  isComplete: boolean;
}

interface CleanupPlan {
  toDelete: KYCRecord[];
  toResetToPending: KYCRecord[];
  toPreserve: KYCRecord[];
  usersToNotify: Set<number>;
}

interface CleanupStats {
  analyzed: number;
  deleted: number;
  resetToPending: number;
  preserved: number;
  notificationsSent: number;
}

const KYCCleanupDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [kycRecords, setKycRecords] = useState<KYCRecord[]>([]);
  const [cleanupPlan, setCleanupPlan] = useState<CleanupPlan | null>(null);
  const [cleanupStats, setCleanupStats] = useState<CleanupStats | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [activeTab, setActiveTab] = useState<'analysis' | 'plan' | 'execution'>('analysis');

  // Analyze current KYC data
  const analyzeKYCData = async () => {
    setLoading(true);
    try {
      console.log('📊 Analyzing KYC data...');

      // Get KYC records with user info via API
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc-cleanup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'analyze' })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      const { kycData, documents } = result.data;

      // Build document map
      const docMap: { [key: string]: Set<string> } = {};
      documents.forEach((doc: any) => {
        if (!docMap[doc.kyc_id]) {
          docMap[doc.kyc_id] = new Set();
        }
        docMap[doc.kyc_id].add(doc.document_type);
      });

      // Process KYC records
      const processedRecords: KYCRecord[] = kycData.map((kyc: any) => ({
        ...kyc,
        documents: Array.from(docMap[kyc.id] || []),
        hasIdDocument: docMap[kyc.id]?.has('id_document') || false,
        hasAddressProof: docMap[kyc.id]?.has('address_proof') || false,
        hasSelfie: docMap[kyc.id]?.has('selfie') || false,
        isComplete: (docMap[kyc.id]?.has('id_document') && docMap[kyc.id]?.has('address_proof')) || false
      }));

      setKycRecords(processedRecords);
      console.log(`✅ Analyzed ${processedRecords.length} KYC records`);

    } catch (error) {
      console.error('❌ Error analyzing KYC data:', error);
      alert('Failed to analyze KYC data: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Create cleanup plan
  const createCleanupPlan = () => {
    console.log('📋 Creating cleanup plan...');

    const plan: CleanupPlan = {
      toDelete: [],
      toResetToPending: [],
      toPreserve: [],
      usersToNotify: new Set()
    };

    kycRecords.forEach(kyc => {
      // Rule 1: Delete Telegram-created KYC records
      if (kyc.created_by_telegram_id) {
        plan.toDelete.push(kyc);
        plan.usersToNotify.add(kyc.user_id);
        return;
      }

      // Rule 2: Delete rejected KYC without complete documents
      if (kyc.kyc_status === 'rejected' && !kyc.isComplete) {
        plan.toDelete.push(kyc);
        plan.usersToNotify.add(kyc.user_id);
        return;
      }

      // Rule 3: Reset completed KYC without complete documents
      if (kyc.kyc_status === 'completed' && !kyc.isComplete) {
        plan.toResetToPending.push(kyc);
        plan.usersToNotify.add(kyc.user_id);
        return;
      }

      // Rule 4: Delete pending KYC without any documents
      if (kyc.kyc_status === 'pending' && kyc.documents.length === 0) {
        plan.toDelete.push(kyc);
        plan.usersToNotify.add(kyc.user_id);
        return;
      }

      // Rule 5: Preserve complete KYC records
      plan.toPreserve.push(kyc);
    });

    setCleanupPlan(plan);
    setActiveTab('plan');
  };

  // Execute cleanup plan
  const executeCleanup = async () => {
    if (!cleanupPlan) return;

    setLoading(true);
    try {
      console.log('🚀 Executing cleanup plan...');

      // Execute cleanup via API
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc-cleanup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'execute_cleanup',
          data: {
            toDelete: cleanupPlan.toDelete,
            toResetToPending: cleanupPlan.toResetToPending
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      const stats: CleanupStats = {
        analyzed: kycRecords.length,
        deleted: result.data.deleted,
        resetToPending: result.data.reset,
        preserved: cleanupPlan.toPreserve.length,
        notificationsSent: cleanupPlan.usersToNotify.size
      };

      setCleanupStats(stats);
      setActiveTab('execution');
      setShowConfirmation(false);

      console.log('✅ Cleanup completed successfully');
      alert('KYC cleanup completed successfully!');

    } catch (error) {
      console.error('❌ Cleanup failed:', error);
      alert('Cleanup failed: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    analyzeKYCData();
  }, []);

  // Get statistics for current records
  const getAnalysisStats = () => {
    const telegramRecords = kycRecords.filter(k => k.created_by_telegram_id);
    const webRecords = kycRecords.filter(k => !k.created_by_telegram_id);
    const completeRecords = kycRecords.filter(k => k.isComplete);
    const incompleteRecords = kycRecords.filter(k => !k.isComplete);

    return {
      total: kycRecords.length,
      telegram: telegramRecords.length,
      web: webRecords.length,
      complete: completeRecords.length,
      incomplete: incompleteRecords.length,
      byStatus: {
        pending: kycRecords.filter(k => k.kyc_status === 'pending').length,
        completed: kycRecords.filter(k => k.kyc_status === 'completed').length,
        rejected: kycRecords.filter(k => k.kyc_status === 'rejected').length
      }
    };
  };

  const stats = getAnalysisStats();

  return (
    <div style={{ padding: '24px', backgroundColor: '#111827', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#ffffff', marginBottom: '8px' }}>
          🧹 KYC Database Cleanup
        </h1>
        <p style={{ color: '#ffffff' }}>
          Clean up KYC records after Telegram-to-Web migration
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', marginBottom: '24px', borderBottom: '1px solid #374151' }}>
        {[
          { id: 'analysis', label: '📊 Analysis', count: stats.total },
          { id: 'plan', label: '📋 Cleanup Plan', count: cleanupPlan ? cleanupPlan.toDelete.length + cleanupPlan.toResetToPending.length : 0 },
          { id: 'execution', label: '🚀 Execution', count: cleanupStats ? cleanupStats.deleted + cleanupStats.resetToPending : 0 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === tab.id ? '#374151' : 'transparent',
              color: activeTab === tab.id ? '#ffffff' : '#9CA3AF',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #F59E0B' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Analysis Tab */}
      {activeTab === 'analysis' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffffff' }}>{stats.total}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Total KYC Records</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F59E0B' }}>{stats.telegram}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Telegram Created</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>{stats.complete}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Complete (with docs)</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EF4444' }}>{stats.incomplete}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Incomplete</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button
              onClick={analyzeKYCData}
              disabled={loading}
              style={{
                backgroundColor: '#374151',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Analyzing...' : '🔄 Refresh Analysis'}
            </button>
            <button
              onClick={createCleanupPlan}
              disabled={loading || kycRecords.length === 0}
              style={{
                backgroundColor: '#F59E0B',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                cursor: (loading || kycRecords.length === 0) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: (loading || kycRecords.length === 0) ? 0.7 : 1
              }}
            >
              📋 Create Cleanup Plan
            </button>
          </div>

          {/* KYC Records Table */}
          <div style={{ backgroundColor: '#1F2937', borderRadius: '8px', border: '1px solid #374151', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#374151' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>User</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Source</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Documents</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Complete</th>
                </tr>
              </thead>
              <tbody>
                {kycRecords.map((kyc) => (
                  <tr key={kyc.id} style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px', color: '#ffffff' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{kyc.users.username}</div>
                        <div style={{ fontSize: '14px', color: '#ffffff' }}>ID: {kyc.user_id}</div>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        backgroundColor: kyc.kyc_status === 'completed' ? '#10B981' + '20' : 
                                       kyc.kyc_status === 'rejected' ? '#EF4444' + '20' : '#F59E0B' + '20',
                        color: kyc.kyc_status === 'completed' ? '#10B981' : 
                               kyc.kyc_status === 'rejected' ? '#EF4444' : '#F59E0B',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        {kyc.kyc_status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: '#ffffff' }}>
                      {kyc.created_by_telegram_id ? '📱 Telegram' : '🌐 Web'}
                    </td>
                    <td style={{ padding: '12px', color: '#ffffff' }}>
                      <div style={{ fontSize: '14px' }}>
                        {kyc.hasIdDocument && '🆔 '}
                        {kyc.hasAddressProof && '🏠 '}
                        {kyc.hasSelfie && '🤳 '}
                        {kyc.documents.length === 0 && '❌ None'}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: kyc.isComplete ? '#10B981' : '#EF4444',
                        fontSize: '14px'
                      }}>
                        {kyc.isComplete ? '✅' : '❌'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cleanup Plan Tab */}
      {activeTab === 'plan' && cleanupPlan && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EF4444' }}>{cleanupPlan.toDelete.length}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Records to Delete</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F59E0B' }}>{cleanupPlan.toResetToPending.length}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Reset to Pending</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>{cleanupPlan.toPreserve.length}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Records to Preserve</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8B5CF6' }}>{cleanupPlan.usersToNotify.size}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Users to Notify</div>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={loading}
              style={{
                backgroundColor: '#EF4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                opacity: loading ? 0.7 : 1
              }}
            >
              🚀 Execute Cleanup Plan
            </button>
          </div>

          {/* Plan Details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            {/* Records to Delete */}
            <div style={{ backgroundColor: '#1F2937', borderRadius: '8px', border: '1px solid #374151', padding: '16px' }}>
              <h3 style={{ color: '#EF4444', marginBottom: '12px', fontSize: '16px' }}>🗑️ Records to Delete</h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {cleanupPlan.toDelete.map(kyc => (
                  <div key={kyc.id} style={{ padding: '8px', borderBottom: '1px solid #374151', fontSize: '14px' }}>
                    <div style={{ color: '#ffffff', fontWeight: '500' }}>{kyc.users.username}</div>
                    <div style={{ color: '#ffffff', fontSize: '14px' }}>
                      {kyc.created_by_telegram_id ? 'Telegram KYC' : `${kyc.kyc_status} - No docs`}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Records to Reset */}
            <div style={{ backgroundColor: '#1F2937', borderRadius: '8px', border: '1px solid #374151', padding: '16px' }}>
              <h3 style={{ color: '#F59E0B', marginBottom: '12px', fontSize: '16px' }}>🔄 Reset to Pending</h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {cleanupPlan.toResetToPending.map(kyc => (
                  <div key={kyc.id} style={{ padding: '8px', borderBottom: '1px solid #374151', fontSize: '14px' }}>
                    <div style={{ color: '#ffffff', fontWeight: '500' }}>{kyc.users.username}</div>
                    <div style={{ color: '#ffffff', fontSize: '14px' }}>
                      Completed but missing docs
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Records to Preserve */}
            <div style={{ backgroundColor: '#1F2937', borderRadius: '8px', border: '1px solid #374151', padding: '16px' }}>
              <h3 style={{ color: '#10B981', marginBottom: '12px', fontSize: '16px' }}>✅ Records to Preserve</h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {cleanupPlan.toPreserve.map(kyc => (
                  <div key={kyc.id} style={{ padding: '8px', borderBottom: '1px solid #374151', fontSize: '14px' }}>
                    <div style={{ color: '#ffffff', fontWeight: '500' }}>{kyc.users.username}</div>
                    <div style={{ color: '#ffffff', fontSize: '14px' }}>
                      {kyc.isComplete ? 'Complete with docs' : 'Manual review needed'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Execution Results Tab */}
      {activeTab === 'execution' && cleanupStats && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffffff' }}>{cleanupStats.analyzed}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Records Analyzed</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EF4444' }}>{cleanupStats.deleted}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Records Deleted</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F59E0B' }}>{cleanupStats.resetToPending}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Reset to Pending</div>
            </div>
            <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>{cleanupStats.preserved}</div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Records Preserved</div>
            </div>
          </div>

          <div style={{ backgroundColor: '#1F2937', borderRadius: '8px', border: '1px solid #374151', padding: '24px' }}>
            <h3 style={{ color: '#10B981', marginBottom: '16px', fontSize: '18px' }}>✅ Cleanup Completed Successfully!</h3>
            <div style={{ color: '#ffffff', lineHeight: '1.6' }}>
              <p>The KYC database cleanup has been completed successfully. Here's what happened:</p>
              <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
                <li>Deleted {cleanupStats.deleted} incomplete KYC records</li>
                <li>Reset {cleanupStats.resetToPending} completed records to pending status</li>
                <li>Preserved {cleanupStats.preserved} complete KYC records</li>
                <li>Sent notifications to {cleanupStats.notificationsSent} affected users</li>
              </ul>
              <p style={{ marginTop: '12px' }}>
                Users with deleted or reset KYC records will need to complete the web-based KYC process with proper document uploads.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            borderRadius: '8px',
            border: '1px solid #374151',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ color: '#EF4444', marginBottom: '16px', fontSize: '20px' }}>⚠️ Confirm KYC Cleanup</h3>
            <div style={{ color: '#ffffff', marginBottom: '20px', lineHeight: '1.6' }}>
              <p>This action will:</p>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Delete {cleanupPlan?.toDelete.length} KYC records permanently</li>
                <li>Reset {cleanupPlan?.toResetToPending.length} records to pending status</li>
                <li>Send notifications to {cleanupPlan?.usersToNotify.size} users</li>
              </ul>
              <p style={{ marginTop: '12px', color: '#EF4444' }}>
                <strong>This action cannot be undone!</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  backgroundColor: '#374151',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeCleanup}
                disabled={loading}
                style={{
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Executing...' : 'Execute Cleanup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCCleanupDashboard;
